import type { ClientPositionView, SavedDestination } from "@solomon-apps/shared";
import { lpProtocolLabel } from "@solomon-apps/shared/src/yaas/lpProtocols";
import { BigDecimal } from "@solomon-labs/bigdecimal";

import { formatObservedUsdv, observedUsdv } from "@/hooks/positionAmounts";
import type { DecimalLike } from "@/hooks/positionDisplayAmount";
import {
    findSavedDestination,
    SAME_AS_SOURCE_LABEL,
    savedDestinationLabel
} from "@/pages/RewardDestinations/destinationInput";
import type { DiscoveredPosition } from "@/services/positionDiscovery";

import { hasExistingPositionForSource } from "./positionSourcePayload";
import type { FindPositionStatus, FoundPositionPreview, OwnershipSourceKind, SubmitReview } from "./steps";
import type { VerifiedSource } from "./types";

/**
 * Pure state derivations the add-position step machine (`useStepFlow`) delegates
 * to. Keeping them here — a sibling to `findPosition.ts` and
 * `positionSourcePayload.ts` — lets the gating, duplicate, and preview decisions
 * be unit-tested by name without mounting the hook. The hook holds the React
 * state; these functions decide what that state means for the flow.
 */

/** The pieces of state the per-step "Next" gate reads. */
export interface StepGateState {
    findStatus: FindPositionStatus;
    matchedSurfaceUuid: string | null;
    verifiedSource: VerifiedSource | null;
    duplicatePositionExists: boolean;
}

/**
 * Per-step gate for "Next". Step 0 (the combined Add Address step) needs
 * a verified source with no existing duplicate — verification can only complete
 * after discovery resolved the holding's Program Surface, so a verified source
 * implies a found, matched holding. Steps 1–2 (rewards, submit) are always
 * satisfiable.
 */
export function isStepSatisfied(state: StepGateState, index: number): boolean {
    if (index === 0) return state.verifiedSource !== null && !state.duplicatePositionExists;
    return true;
}

/**
 * Whether the current step may advance. Same gate as `isStepSatisfied`, plus
 * step 0's extra wait for the discovered-holding preview to settle
 * (`foundPosition`): the user must never advance past the combined step while
 * the balance read that gates the preview is still in flight.
 */
export function canAdvanceStep(
    state: StepGateState,
    active: number,
    foundPosition: FoundPositionPreview | null
): boolean {
    return isStepSatisfied(state, active) && (active !== 0 || foundPosition !== null);
}

/**
 * The address a position would be submitted under (= `walletAddress`), the same
 * value the duplicate check and the API key on. Mirrors `sourceAddress()`:
 *   - EOA → the entered wallet, falling back to the connected wallet;
 *   - multisig → the multisig account (never the vault — a user may paste either
 *     the account or the vault, but both canonicalize to the account upstream);
 *   - null while the source is unresolved.
 */
export function resolveCandidateSourceAddress(args: {
    sourceKind: OwnershipSourceKind | null;
    enteredAddress: string;
    connectedWalletAddress: string;
    multisigAddress: string | null;
}): string | null {
    if (args.sourceKind === "multisig-squads") return args.multisigAddress ?? null;
    if (args.sourceKind === "eoa") return args.enteredAddress.trim() || args.connectedWalletAddress || null;
    return null;
}

/**
 * A position for this (Program Surface, source) pair already exists, so the
 * backend would reject the submit as a conflict. Surfacing it at Verify lets the
 * user fix it before signing. Hidden positions are excluded from the client
 * list, so the submit-time guard remains the backstop for those.
 */
export function hasDuplicatePosition(args: {
    matchedSurfaceUuid: string | null;
    candidateSourceAddress: string | null;
    existingPositions: ClientPositionView[];
}): boolean {
    return (
        args.matchedSurfaceUuid !== null &&
        args.candidateSourceAddress !== null &&
        hasExistingPositionForSource(args.existingPositions, args.matchedSurfaceUuid, args.candidateSourceAddress)
    );
}

/**
 * The pasted Position Source already has a position with this Company
 * Recipient, on any Program Surface. Decided at the source step, as soon as the
 * field is filled and classified: no connected wallet and no discovery needed.
 * Keys on the address the API keys on: the entered EOA wallet, or the multisig
 * account for a Squads source (a pasted vault resolves to its account). The
 * entered EOA is used as-is, never the connected-wallet fallback, since the
 * check is about what the user pasted. The surface-scoped
 * `hasDuplicatePosition` stays as the discovery-time backstop.
 */
export function isSourceAlreadyRegistered(args: {
    sourceKind: OwnershipSourceKind | null;
    enteredAddress: string;
    multisigAddress: string | null;
    existingPositions: ClientPositionView[];
}): boolean {
    const key =
        args.sourceKind === "eoa"
            ? args.enteredAddress.trim()
            : args.sourceKind === "multisig-squads"
              ? (args.multisigAddress ?? "")
              : "";
    if (key.length === 0) return false;
    return args.existingPositions.some((position) => position.account.address === key);
}

interface AddressBalancePreviewInput {
    liveUsdv: DecimalLike | null;
    isLoadingLiveUsdv: boolean;
    hasFetchedLiveUsdv: boolean;
    holdingUsdvError: Error | null;
}

/**
 * Read-only Address balance, or null while the live USDv
 * read hasn't settled (so the block appears exactly once, already in its final
 * funded / empty / unavailable form — no "..." flash, no funded-to-empty swap).
 *
 * Zero balance is a registerable state: the empty wallet renders the same
 * Positions / Balance card with USDv at $0.00. `zeroBalance` is true
 * when the live read succeeds at 0 (or succeeds with no USDv token account). A
 * read error is never treated as zero (`balanceLabel` shows "—").
 * This preview does not establish a Program match or ownership.
 */
export function resolveAddressBalancePreview(args: AddressBalancePreviewInput): FoundPositionPreview | null {
    const settled = args.hasFetchedLiveUsdv || args.holdingUsdvError !== null;
    if (!settled) return null;
    return {
        observedAmount: args.holdingUsdvError ? null : (args.liveUsdv?.toString() ?? "0"),
        balanceLabel: formatObservedUsdv(args.holdingUsdvError ? null : observedUsdv(args.liveUsdv?.toString() ?? "0")),
        zeroBalance:
            args.holdingUsdvError === null &&
            (args.liveUsdv !== null ? observedUsdv(args.liveUsdv.toString())?.eq(0) === true : args.hasFetchedLiveUsdv)
    };
}

/** Enrollment previews additionally require an authorized backend discovery. */
export function resolveFoundPositionPreview(
    args: AddressBalancePreviewInput & {
        findStatus: FindPositionStatus;
        discovered: DiscoveredPosition | null;
    }
): FoundPositionPreview | null {
    if (args.findStatus !== "found" || args.discovered === null) return null;
    return resolveAddressBalancePreview(args);
}

/**
 * Display-ready enrollment summary — the last step's receipt (see
 * `SubmitReview`): how many positions discovery surfaced on the address, their
 * total USDv, and the (truncated) rewards destination. Null while discovery or
 * verification is unsettled (the step gates guarantee both before the last step
 * is reachable, so this is a type-level guard, not a reachable UI state).
 */
export function resolveSubmitReview(args: {
    foundPosition: FoundPositionPreview | null;
    verifiedSource: VerifiedSource | null;
    destination: string;
    defaultDestinationAddress: string;
    /** The default's name: a saved destination's label, "Same as Position
     *  Source", or "Multisig Vault" for a Squads source; empty for none. */
    defaultDestinationLabel?: string;
    /** Saved Rewards Destinations, to name the chosen one in the receipt. */
    savedDestinations?: SavedDestination[];
}): SubmitReview | null {
    const { foundPosition, verifiedSource } = args;
    if (foundPosition === null || verifiedSource === null) return null;

    const resolvedDestination = args.destination.trim() || args.defaultDestinationAddress;

    return {
        // The grouped preview retains the count of individual discovered holdings.
        totalPositions: foundPosition.totalPositions ?? 1,
        totalBalanceLabel: foundPosition.balanceLabel,
        rewardsDestinationAddress: resolvedDestination,
        rewardsDestinationLabel: ((): string | null => {
            const saved = findSavedDestination(args.savedDestinations ?? [], resolvedDestination);
            if (saved) return savedDestinationLabel(saved) || null;
            // Nothing picked (or the Squads vault auto-fill, which is the
            // default) is named as the picker names it.
            return resolvedDestination === args.defaultDestinationAddress
                ? (args.defaultDestinationLabel ?? SAME_AS_SOURCE_LABEL) || null
                : null;
        })()
    };
}

/**
 * The state reset applied when the source address is edited. Editing the source
 * invalidates discovery AND any captured verification (the signed message binds
 * the old surface's UUID), so both step gates fall back to unsatisfied and the
 * user cannot advance on a stale proof. The caller additionally bumps the
 * discovery run id and cancels the in-flight verification flow — the imperative
 * effects this value can't express.
 */
export interface AddressEditResetState {
    matchedSurfaceUuid: null;
    findStatus: "idle";
    discovered: null;
    submitError: null;
    verifyStatus: "idle";
    verifiedSource: null;
}

export function addressEditResetState(): AddressEditResetState {
    return {
        matchedSurfaceUuid: null,
        findStatus: "idle",
        discovered: null,
        submitError: null,
        verifyStatus: "idle",
        verifiedSource: null
    };
}

/** Group previews by surface and pool while retaining every individual LP identity. */
export function resolveHoldingsPreview(args: {
    holdings: DiscoveredPosition[];
    addressPreview: FoundPositionPreview;
    lpDiscoveryComplete: boolean;
}): FoundPositionPreview {
    const groups = new Map<string, { label: string; surface: string; count: number; amount: BigDecimal | null }>();
    const identities = new Set<string>();
    let count = 0;
    for (const holding of args.holdings) {
        if (holding.match.kind !== "matched" && holding.surfaceTypeSlug !== "wallet-account") continue;
        const lp = holding.lpPosition;
        const identity = `${holding.surfaceTypeSlug}:${lp?.poolAddress ?? "address"}:${lp?.positionAddress ?? holding.instance.address}`;
        if (identities.has(identity)) continue;
        identities.add(identity);
        const key = `${holding.surfaceTypeSlug}:${lp?.poolAddress ?? "address"}`;
        const amount =
            lp && holding.observation?.completeness !== "complete" ? null : observedUsdv(holding.observedBalance);
        const previous = groups.get(key);
        groups.set(key, {
            // yaas-api names the pool from Jachin's catalog; the console holds no pool addresses.
            label: lp ? (lp.pairLabel ?? lpProtocolLabel(lp.protocol)) : "USDv",
            surface: lp ? lpProtocolLabel(lp.protocol) : "Address",
            count: (previous?.count ?? 0) + 1,
            amount: previous
                ? previous.amount === null || amount === null
                    ? null
                    : previous.amount.add(amount)
                : amount
        });
        count += 1;
    }
    // Address live read is authoritative for its row and for the aggregate.
    const addressRow = [...groups.entries()].find(([, group]) => group.surface === "Address");
    const rows = [...groups.entries()].map(([key, group]) => ({
        key,
        label: group.label,
        surface: group.surface,
        positionCount: group.count,
        balanceLabel: key === addressRow?.[0] ? args.addressPreview.balanceLabel : formatObservedUsdv(group.amount)
    }));
    const addressAmount = addressRow ? observedUsdv(args.addressPreview.observedAmount) : new BigDecimal(0);
    const lpTotal = [...groups.values()]
        .filter((group) => group.surface !== "Address")
        .reduce<BigDecimal | null>(
            (sum, group) => (sum === null || group.amount === null ? null : sum.add(group.amount)),
            new BigDecimal(0)
        );
    const incomplete = !args.lpDiscoveryComplete || addressAmount === null || lpTotal === null;
    // Total only the discovered holdings. Scan-wide coverage can be partial
    // while every displayed principal is known; unknown row amounts still
    // prevent a total. Keep coverage metadata separate from the observed sum.
    return {
        rows,
        totalPositions: count,
        balanceLabel: addressAmount === null || lpTotal === null ? "—" : formatObservedUsdv(addressAmount.add(lpTotal)),
        zeroBalance: args.addressPreview.zeroBalance,
        incomplete
    };
}
